// Pop-up: "Explain the difference" for one customer (2026-10-27).
// Checks every closed period: the copy saved when the period was closed
// (what the customer was sent) vs today's numbers, and lists exactly
// which tips, invoices and adjustments changed, with who/when/why where
// the system recorded it. Calls /api/balances/explain one period at a
// time so a long history never times out.
// Part of the Balances page (components/Balances.jsx).
import { useEffect, useState } from 'react';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const money = (v) => {
  const n = Number(v) || 0;
  const s = '$' + Math.abs(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? '(' + s + ')' : s;
};
const signed = (v) => (Number(v) > 0 ? '+' : Number(v) < 0 ? '−' : '') + money(Math.abs(Number(v) || 0)).replace(/[()]/g, '');
const day = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? MON[+m[2] - 1] + ' ' + (+m[3]) + ', ' + m[1] : String(iso || '');
};
const when = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return isNaN(d) ? String(ts) : d.toLocaleString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const period = (c) => {
  const a = String(c.start || ''), b = String(c.end || '');
  return day(a).replace(/, \d{4}$/, '') + ' – ' + day(b);
};

const TOTAL_LABELS = {
  opening: 'Opening balance', tips: 'Tips charged', tipsCount: 'Number of tips',
  disposal: 'Disposal fees invoiced', other: 'Other invoiced', adjustments: 'Adjustments', ending: 'Ending balance'
};

// One edit-history line from row_audit_log
function HistoryLine({ h }) {
  const ch = h.changes || {};
  const fields = Object.keys(ch).filter(k => ch[k] && typeof ch[k] === 'object' && 'to' in ch[k])
    .map(k => k + ': ' + JSON.stringify(ch[k].from) + ' → ' + JSON.stringify(ch[k].to));
  return (
    <div className="small" style={{ color: '#475569', marginLeft: '1rem' }}>
      ✎ {when(h.changed_at)} · <strong>{h.changed_by || 'unknown'}</strong> · {h.source}
      {fields.length > 0 && <> · {fields.join('; ')}</>}
      {h.reason && <> · “{h.reason}”</>}
    </div>
  );
}

function TipLine({ kind, t }) {
  const r = t.after || t.before;
  const colour = kind === 'added' ? '#166534' : kind === 'removed' ? '#b91c1c' : '#92400e';
  return (
    <div style={{ padding: '0.3rem 0', borderBottom: '1px dashed #e2e8f0' }}>
      <span style={{ color: colour, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75em', marginRight: 6 }}>
        {kind === 'added' ? 'Added' : kind === 'removed' ? 'Removed' : 'Changed'}
      </span>
      Tip {r.date} {r.time || ''} · <code>{r.plate}</code> · {r.tier}{r.tankSize ? ' (' + r.tankSize + 'm³)' : ''} ·{' '}
      {kind === 'changed'
        ? t.fields.map(f => <span key={f}>{f}: <s>{f === 'rate' ? money(t.before[f]) : String(t.before[f])}</s> → <strong>{f === 'rate' ? money(t.after[f]) : String(t.after[f])}</strong>{' '}</span>)
        : <strong>{money(r.rate)}</strong>}
      <span className="small" style={{ color: '#94a3b8' }}> · tip id {r.id}</span>
      {t.now && (
        <div className="small" style={{ marginLeft: '1rem', color: '#475569' }}>
          Now: {t.now.status}{t.now.customer ? ' · assigned to ' + t.now.customer : ''}
        </div>
      )}
      {(t.history || []).map((h, i) => <HistoryLine key={i} h={h} />)}
      {kind !== 'added' && (t.history || []).length === 0 && !t.now && (
        <div className="small" style={{ marginLeft: '1rem', color: '#94a3b8' }}>No edit recorded on this tip since the close (likely a price or plate/customer change, see below).</div>
      )}
    </div>
  );
}

function InvoiceLine({ kind, i }) {
  const r = i.after || i.before;
  const colour = kind === 'added' ? '#166534' : kind === 'removed' ? '#b91c1c' : '#92400e';
  return (
    <div style={{ padding: '0.3rem 0', borderBottom: '1px dashed #e2e8f0' }}>
      <span style={{ color: colour, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75em', marginRight: 6 }}>
        {kind === 'added' ? 'Added' : kind === 'removed' ? 'Removed' : 'Changed'}
      </span>
      QuickBooks invoice <strong>#{r.number || r.id}</strong> dated {day(r.date)} ·{' '}
      {kind === 'changed'
        ? i.fields.map(f => <span key={f}>{f}: <s>{typeof i.before[f] === 'number' ? money(i.before[f]) : String(i.before[f])}</s> → <strong>{typeof i.after[f] === 'number' ? money(i.after[f]) : String(i.after[f])}</strong>{' '}</span>)
        : <>disposal <strong>{money(r.disposal)}</strong>{r.other ? <> · other {money(r.other)}</> : null} · total {money(r.totalAmt)}</>}
      <span className="small" style={{ color: '#94a3b8' }}> · QuickBooks id {r.id}</span>
      {i.qbo && (
        <div className="small" style={{ marginLeft: '1rem', color: '#475569' }}>
          QuickBooks customer: <strong>{i.qbo.customerName || '—'}</strong>
          {i.qbo.created && <> · created {when(i.qbo.created)}</>}
          {i.qbo.updated && <> · last changed {when(i.qbo.updated)}</>}
        </div>
      )}
    </div>
  );
}

function PeriodCard({ r }) {
  const t = r.totals || {};
  const changedTotals = Object.keys(TOTAL_LABELS).filter(k => t[k] && Math.abs(t[k].delta) >= 0.005);
  const tips = r.tips, inv = r.invoices, adj = r.adjustments;
  const openingOnly = changedTotals.every(k => k === 'opening' || k === 'ending')
    && t.opening && Math.abs(t.opening.delta) >= 0.005
    && t.ending && Math.abs(t.ending.delta - t.opening.delta) < 0.005;
  return (
    <div className="chart-card" style={{ padding: '0.6rem 0.8rem', marginBottom: '0.6rem', borderLeft: '4px solid ' + (openingOnly ? '#cbd5e1' : '#f59e0b') }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>{period(r.cycle)}</strong>
        <span className="small" style={{ color: '#64748b' }}>closed {when(r.cycle.closedAt)}{r.cycle.closedBy ? ' by ' + r.cycle.closedBy : ''}</span>
      </div>
      {openingOnly && (
        <div className="small" style={{ color: '#64748b', marginTop: 4 }}>
          Nothing in this period changed. Its opening balance moved by {signed(t.opening.delta)}, carried forward from an earlier period shown above.
        </div>
      )}
      {!openingOnly && changedTotals.length > 0 && (
        <table className="fs-table tidy" style={{ marginTop: 6, width: 'auto' }}>
          <thead><tr><th>Line</th><th className="num">Saved at close</th><th className="num">Today</th><th className="num">Difference</th></tr></thead>
          <tbody>
            {changedTotals.map(k => (
              <tr key={k}>
                <td>{TOTAL_LABELS[k]}</td>
                <td className="num">{k === 'tipsCount' ? t[k].saved : money(t[k].saved)}</td>
                <td className="num">{k === 'tipsCount' ? t[k].today : money(t[k].today)}</td>
                <td className="num" style={{ fontWeight: 700 }}>{k === 'tipsCount' ? (t[k].delta > 0 ? '+' : '') + t[k].delta : signed(t[k].delta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {inv && (inv.added.length + inv.removed.length + inv.changed.length > 0) && (
        <div style={{ marginTop: 6 }}>
          {inv.added.map((i, k) => <InvoiceLine key={'ia' + k} kind="added" i={i} />)}
          {inv.removed.map((i, k) => <InvoiceLine key={'ir' + k} kind="removed" i={i} />)}
          {inv.changed.map((i, k) => <InvoiceLine key={'ic' + k} kind="changed" i={i} />)}
        </div>
      )}
      {tips && (tips.added.length + tips.removed.length + tips.changed.length > 0) && (
        <div style={{ marginTop: 6 }}>
          {tips.added.map((x, k) => <TipLine key={'ta' + k} kind="added" t={x} />)}
          {tips.removed.map((x, k) => <TipLine key={'tr' + k} kind="removed" t={x} />)}
          {tips.changed.map((x, k) => <TipLine key={'tc' + k} kind="changed" t={x} />)}
        </div>
      )}
      {adj && (adj.added.length + adj.removed.length + adj.changed.length > 0) && (
        <div style={{ marginTop: 6 }}>
          {[...adj.added.map(a => ['Added', a]), ...adj.removed.map(a => ['Removed', a]), ...adj.changed.map(a => ['Changed', a])].map(([k, a], n) => {
            const x = a.after || a.before;
            return (
              <div key={'ad' + n} style={{ padding: '0.3rem 0', borderBottom: '1px dashed #e2e8f0' }}>
                <span style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75em', marginRight: 6 }}>{k}</span>
                Adjustment {money(x.amount)} · {x.description}
                {(a.history || []).map((h, i) => <HistoryLine key={i} h={h} />)}
              </div>
            );
          })}
        </div>
      )}
      {r.rateChanges && r.rateChanges.length > 0 && !openingOnly && (
        <div className="small" style={{ marginTop: 6, color: '#475569' }}>
          Price entries made for this customer after the close:
          {r.rateChanges.map(rc => (
            <div key={rc.id} style={{ marginLeft: '1rem' }}>
              {when(rc.created_at)} · {rc.created_by || 'unknown'} · effective {day(rc.effective_from)}{rc.notes ? ' · ' + rc.notes : ''}
            </div>
          ))}
        </div>
      )}
      {(!r.itemDetailSaved.tips || !r.itemDetailSaved.invoices) && (
        <div className="small" style={{ marginTop: 6, color: '#94a3b8' }}>
          The saved copy for this period has totals only{!r.itemDetailSaved.tips ? ' (no tip list)' : ''}{!r.itemDetailSaved.invoices ? ' (no invoice list)' : ''}, so individual items can't be compared.
        </div>
      )}
    </div>
  );
}

export default function ExplainModal({ explain, setExplain }) {
  const [list, setList] = useState(null);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!explain) return;
    let cancelled = false;
    setList(null); setResults([]); setError(null); setProgress('Finding closed periods…');
    (async () => {
      try {
        const base = '/api/balances/explain?customer=' + encodeURIComponent(explain.customer);
        const l = await fetch(base, { credentials: 'include' }).then(r => r.json());
        if (!l.ok) throw new Error(l.error || 'failed');
        if (cancelled) return;
        setList(l.cycles);
        const out = [];
        for (let i = 0; i < l.cycles.length; i++) {
          const c = l.cycles[i];
          setProgress('Checking ' + period(c) + ' (' + (i + 1) + ' of ' + l.cycles.length + ')…');
          const r = await fetch(base + '&cycle=' + encodeURIComponent(c.cycle_key), { credentials: 'include' }).then(x => x.json());
          if (cancelled) return;
          if (r.ok) { out.push(r); setResults([...out]); }
        }
        setProgress('');
      } catch (e) { if (!cancelled) { setError(e.message); setProgress(''); } }
    })();
    return () => { cancelled = true; };
  }, [explain]);

  if (!explain) return null;
  const changed = results.filter(r => r.changed);
  const latest = results.length ? results[results.length - 1] : null;
  return (
    <div className="modal-backdrop" onClick={() => setExplain(null)}>
      <div className="modal-card" onClick={e => e.stopPropagation()} style={{ minWidth: 760, maxWidth: '95vw', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>{explain.customer} — explain the difference</h3>
          <button className="refresh-btn" onClick={() => setExplain(null)}>Close</button>
        </div>
        <div className="small" style={{ color: '#64748b', margin: '0.3rem 0 0.7rem' }}>
          Compares each closed period's saved copy (what the statement showed when the period was closed) with today's numbers.
        </div>
        {error && <div style={{ color: '#b91c1c' }}>Error: {error}</div>}
        {progress && <div className="small" style={{ color: '#0369a1', marginBottom: 8 }}>⏳ {progress}</div>}
        {!progress && list && list.length === 0 && <div>No closed periods yet.</div>}
        {!progress && results.length > 0 && changed.length === 0 && (
          <div style={{ color: '#166534', fontWeight: 600 }}>✓ No differences. Every closed period still matches what was saved.</div>
        )}
        {!progress && changed.length > 0 && latest && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '0.5rem 0.7rem', marginBottom: 10 }}>
            {changed.length} closed period{changed.length > 1 ? 's' : ''} changed. Ending balance for {period(latest.cycle)}:
            saved <strong>{money(latest.totals.ending.saved)}</strong>, today <strong>{money(latest.totals.ending.today)}</strong>
            {' '}({signed(latest.totals.ending.delta)}). The first card below is where the change started.
          </div>
        )}
        {changed.map(r => <PeriodCard key={r.cycle.key} r={r} />)}
      </div>
    </div>
  );
}
